'use client';

import { Tick } from '@ps/web-fusion-icons';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { Checkbox as AriaCheckbox } from 'react-aria-components';
import type { CheckboxProps } from 'react-aria-components';

import Icon from '../Icon/Icon';
import { useFusionTheme } from '../hooks';

import styles from './Checkbox.module.scss';

export interface Props
  extends Pick<
      CheckboxProps,
      | 'name'
      | 'value'
      | 'isSelected'
      | 'defaultSelected'
      | 'isDisabled'
      | 'className'
      | 'onChange'
      | 'onFocusChange'
      | 'onHoverChange'
      | 'inputRef'
    >,
    VariantProps<typeof checkboxVariants> {
  children: ReactNode;
  subtext?: string;
  error?: string;
}

const checkboxVariants = cva(styles.container, {
  variants: {
    size: {
      small: styles.sizeSmall,
      default: styles.sizeDefault
    }
  }
});

const Checkbox = ({ size = 'default', subtext, error, children, className, ...props }: Props) => {
  const { addThemeClassName } = useFusionTheme();
  const hasError = !!error;
  const iconSize = size === 'default' ? 'default' : 'sm';

  return (
    <AriaCheckbox
      {...props}
      isInvalid={hasError}
      className={addThemeClassName(checkboxVariants({ size }), className)}
    >
      <div className={styles.checkboxContainer}>
        <div className={styles.checkbox}>
          <Icon className={styles.icon} icon={Tick} size={iconSize} aria-hidden />
        </div>
        <div className={styles.labels}>
          <div className={styles.label}>{children}</div>
          {subtext && !hasError && <div className={styles.subtitle}>{subtext}</div>}
        </div>
      </div>
      {hasError && <div className={styles.errorLabel}>{error}</div>}
    </AriaCheckbox>
  );
};

export default Checkbox;
